'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { Form, Formik, useFormikContext } from 'formik'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import type { BillingPlan } from '@/lib/billing/types'
import { diffPlanSettings } from '@/lib/billing/plan-changes'
import { PlanConflictError } from '@/lib/billing/plans/plan-write'
import { getSECTIONS, type SectionId } from './catalog'
import { createEditorValues, fieldSection, pickPlanFormData, serializeEditor, validateEditor, type PlanEditorValues } from './model'
import { EditorSection } from './sections'
import { PlanHistoryButton } from './history-dialog'
import { PlanSummary } from './plan-summary'
import { ReviewDialog, type PlanReview } from './review-dialog'
import type { PlanPayload } from './types'
import { useUnsavedChanges } from './use-unsaved-changes'
import { usePlanPresentation } from "./use-plan-presentation"
import styles from './plan-editor.module.css'
import { useTranslations } from "next-intl"




interface PlanEditorProps {
  plan: BillingPlan | null
  /** The server rejects the save if the plan's `updated_at` moved past `expectedUpdatedAt`. */
  onSave: (data: PlanPayload, expectedUpdatedAt: string | null) => Promise<void>
  onClose: () => void
  /** Discards the draft and reopens on `plan`. */
  onReload: (plan: BillingPlan) => void
}

export function PlanEditor({ plan, onSave, onClose, onReload }: PlanEditorProps) {
  const presentation = usePlanPresentation()
  const tUi = useTranslations("Billing.BillingPlanEditorPlanEditor")
  const [initialValues] = useState(() => createEditorValues(plan))
  // Raw saved plan (before normalization): the diff base and the concurrency token.
  const [basePlan, setBasePlan] = useState(plan)
  const [review, setReview] = useState<PlanReview | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [conflict, setConflict] = useState<PlanConflictError | null>(null)

  async function confirm() {
    if (!review || saving) return
    setSaving(true)
    setSaveError(null)
    try {
      await onSave(review.payload, basePlan?.updated_at ?? null)
      setReview(null)
    } catch (error) {
      if (error instanceof PlanConflictError) setConflict(error)
      else setSaveError(error instanceof Error ? error.message : tUi('failedToSavePlan'))
    } finally {
      setSaving(false)
    }
  }

  function back() {
    setReview(null)
    setConflict(null)
  }

  return (
    <Formik
      initialValues={initialValues}
      validate={values => validateEditor(values, presentation)}
      // Must be async: Formik keeps `isSubmitting` set after a synchronous handler.
      onSubmit={async values => {
        const payload = serializeEditor(values)
        setSaveError(null)
        setReview({ payload, changes: basePlan ? diffPlanSettings(basePlan, payload) : null })
      }}
    >
      <>
        <EditorContent planId={plan?.id} basePlan={basePlan} saving={saving} onSynced={setBasePlan} onClose={onClose} />
        <ReviewDialog
          review={review}
          saving={saving}
          error={saveError}
          conflict={conflict}
          onConfirm={confirm}
          onBack={back}
          onReload={() => { if (conflict) onReload(conflict.plan) }}
        />
      </>
    </Formik>
  )
}

interface EditorContentProps {
  planId?: string
  basePlan: BillingPlan | null
  saving: boolean
  onSynced: (plan: BillingPlan) => void
  onClose: () => void
}

function EditorContent({ planId, basePlan, saving, onSynced, onClose }: EditorContentProps) {
  const SECTIONS = getSECTIONS(useTranslations("PlanCatalog"))



  const tUi = useTranslations("Billing.BillingPlanEditorPlanEditor")
  const tCommonUi = useTranslations("Common")

  const { values, errors, dirty, isSubmitting, isValidating, submitCount, resetForm } = useFormikContext<PlanEditorValues>()
  const [section, setSection] = useState<SectionId>('overview')
  const [discardOpen, setDiscardOpen] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [changedElsewhere, setChangedElsewhere] = useState(false)
  const handledSubmit = useRef(0)
  const root = useRef<HTMLDivElement>(null)
  const busy = isSubmitting || syncing || saving
  const unchanged = useMemo(() => hasNoChanges(basePlan, values), [basePlan, values])
  const pending = planId ? !unchanged : dirty
  const current = SECTIONS.find(item => item.id === section)!
  const invalidSections = new Set(Object.keys(errors).map(fieldSection))
  useUnsavedChanges(dirty, busy)

  useEffect(() => {
    if (!submitCount || isValidating || isSubmitting || submitCount === handledSubmit.current) return
    handledSubmit.current = submitCount
    const firstError = Object.keys(errors)[0]
    if (!firstError) return
    setSection(fieldSection(firstError))
    const frame = requestAnimationFrame(() => root.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus())
    return () => cancelAnimationFrame(frame)
  }, [submitCount, isValidating, isSubmitting, errors])

  function close() {
    if (busy) return
    if (dirty) setDiscardOpen(true)
    else onClose()
  }

  // Sync needs a clean form, so reloading every value loses no draft.
  function applySync(synced: BillingPlan) {
    setChangedElsewhere(basePlan !== null && Object.keys(diffPlanSettings(basePlan, pickPlanFormData(synced))).length > 0)
    resetForm({ values: createEditorValues(synced) })
    onSynced(synced)
  }

  return <div ref={root} className={styles.editor}>
    <Form noValidate className="space-y-5">
      <div className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-4 rounded-lg border bg-card p-4 shadow-sm">
        <div className="min-w-0 flex-1 basis-64 space-y-2">
          <Button type="button" variant="ghost" size="sm" className="px-0" onClick={close} disabled={busy}>
            <ArrowLeft className="mr-2 h-4 w-4" /><span key={tUi("backToPlans")}>{tUi("backToPlans")}</span></Button>
          <h1 key={planId ? tUi("editPlan") : tUi("createPlan")} className="text-2xl font-semibold">{planId ? tUi("editPlan") : tUi("createPlan")}</h1>
          {/* A single text node, so it keeps updating after browser translation. */}
          <p className="text-sm text-muted-foreground">{<span key={tUi("value0Value1", { value0: String(values.display_name || 'New plan'), value1: String(pending ? 'Unsaved changes' : 'No unsaved changes') })}>{tUi("value0Value1", { value0: String(values.display_name || 'New plan'), value1: String(pending ? 'Unsaved changes' : 'No unsaved changes') })}</span>}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {planId ? <PlanHistoryButton planId={planId} /> : null}
          <Button type="button" variant="outline" onClick={close} disabled={busy}><span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
          <Button type="submit" disabled={busy || unchanged}>
            {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            <span key={planId ? tUi("saveChanges") : tUi("createPlan")}>{planId ? tUi("saveChanges") : tUi("createPlan")}</span>
          </Button>
        </div>
      </div>
      {changedElsewhere ? <p role="status" className="rounded-md border p-3 text-sm"><span key={tUi("thisPlanWasChangedElsewhereTheEditorNow")}>{tUi("thisPlanWasChangedElsewhereTheEditorNow")}</span></p> : null}
      {submitCount > 0 && Object.keys(errors).length > 0 ? <p role="alert" className="text-sm text-destructive"><span key={tUi("correctTheHighlightedFieldsBeforeSaving")}>{tUi("correctTheHighlightedFieldsBeforeSaving")}</span></p> : null}
      <div className={styles.mobileNavigation}>
        <Label htmlFor="plan-editor-section"><span key={tUi("section")}>{tUi("section")}</span></Label>
        <select
          id="plan-editor-section"
          className="mt-2 h-10 w-full rounded-md border bg-background px-3 text-sm"
          value={section}
          disabled={busy}
          onChange={event => setSection(event.target.value as SectionId)}
        >
          {SECTIONS.map(item => (
            <option key={[item.id, tUi("value0Value12", { value0: String(item.title), value1: String(submitCount > 0 && invalidSections.has(item.id) ? tUi('needsAttentionSuffix') : '') })].join(":")} value={item.id}>{tUi("value0Value12", { value0: String(item.title), value1: String(submitCount > 0 && invalidSections.has(item.id) ? tUi('needsAttentionSuffix') : '') })}</option>
          ))}
        </select>
      </div>
      <div className={styles.layout}>
        <nav aria-label={tUi("planSettings")} className={styles.navigation}>
          {SECTIONS.map(item => (
            <button
              key={item.id}
              type="button"
              disabled={busy}
              aria-current={section === item.id ? 'page' : undefined}
              onClick={() => setSection(item.id)}
              className={`rounded-md p-3 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:opacity-50 ${section === item.id ? 'bg-violet-600 font-semibold text-white shadow-sm hover:bg-violet-700 dark:hover:bg-violet-500' : 'text-foreground hover:bg-muted'}`}
            >
              <span className="block">
                <span>{item.title}</span>
                {submitCount > 0 && invalidSections.has(item.id) ? <span className="ml-1 text-destructive" aria-label={tUi("needsAttention")}>•</span> : null}
              </span>
              <span className={`mt-1 block text-xs font-normal ${section === item.id ? 'text-white' : 'text-muted-foreground'}`}>{item.detail}</span>
            </button>
          ))}
        </nav>
        <section aria-labelledby="plan-section-title" className="min-w-0 space-y-4">
          <div>
            <h2 id="plan-section-title" className="text-lg font-semibold">{current.title}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{current.description}</p>
          </div>
          <fieldset disabled={busy} className="min-w-0 space-y-4">
            <EditorSection section={section} planId={planId} onSyncingChange={setSyncing} onSynced={applySync} />
          </fieldset>
        </section>
        <aside className={styles.summary} aria-label={tUi("planSummary")}><PlanSummary /></aside>
      </div>
    </Form>
    <AlertDialog open={discardOpen} onOpenChange={setDiscardOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle><span key={tUi("discardUnsavedChanges")}>{tUi("discardUnsavedChanges")}</span></AlertDialogTitle>
          <AlertDialogDescription><span key={tUi("yourChangesToThisPlanHaveNotBeen")}>{tUi("yourChangesToThisPlanHaveNotBeen")}</span></AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel><span key={tUi("continueEditing")}>{tUi("continueEditing")}</span></AlertDialogCancel>
          <AlertDialogAction onClick={onClose}><span key={tUi("discard")}>{tUi("discard")}</span></AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>
}

// Not Formik `dirty`, which misses the voice-flag normalization.
function hasNoChanges(basePlan: BillingPlan | null, values: PlanEditorValues): boolean {
  if (!basePlan || Object.keys(validateEditor(values)).length) return false
  return Object.keys(diffPlanSettings(basePlan, serializeEditor(values))).length === 0
}
